import { FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useUserSettings } from "../../../context/userSettingsContext";
import { useI18n } from "../../../hooks/useTranslations";
import SvgBuild from "../../icons/Build";
import SvgCalendar from "../../icons/CalendarMonth";
import SvgCarCrash from "../../icons/CarCrash";
import SvgCarTag from "../../icons/CarTag";
import SvgPin from "../../icons/Pin";
import SvgSwapDrivingApps from "../../icons/SwapDrivingApps";
import SvgTask from "../../icons/Task";
import SvgTrafficJam from "../../icons/TrafficJam";
import SvgWarning from "../../icons/Warning";
import VpnKey from "../../icons/VpnKey";
import Nav, { NavItemNotification } from "../Nav";

type CarsMenuProps = {
  closeSidebar: () => void;
};

const CarsMenu: FC<CarsMenuProps> = ({ closeSidebar }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  // const carsApi = useAdminCarsApi();
  // const aborter = useRef(new AbortController());
  const navigate = useNavigate();

  // const notifications = carsApi.useCarNotification(aborter.current.signal);
  // const [userSettings.notifications, isNotificationInitialized] = useDataInitializer(
  //   notifications.data,
  //   notifications.error,
  // );

  const administrationCars: ReactNode[] = [];
  if (userSettings.permissions.carDrivers.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/car_drivers"
        text={i18n.t.carDrivers.plural}
        to="/admin/car_drivers"
        icon={SvgSwapDrivingApps}
        onClick={closeSidebar}
      />,
    );
  }

  const taskNotification: NavItemNotification | undefined =
    userSettings.notifications?.taskToDo && userSettings.notifications.taskToDo > 0
      ? {
          number: userSettings.notifications?.taskToDo,
          color: "red",
          onClick: () => navigate("/admin/car_tasks/only_to_do"),
        }
      : undefined;

  if (userSettings.permissions.carTasks.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/car_tasks"
        text={i18n.t.carTasks.plural}
        to="/admin/car_tasks"
        icon={SvgTask}
        onClick={closeSidebar}
        notification={taskNotification}
      />,
    );
  }

  const dueDateNotification: NavItemNotification | undefined = userSettings.notifications?.dueDateExpired
    ? userSettings.notifications?.dueDateExpired > 0
      ? {
          number: userSettings.notifications?.dueDateExpired,
          color: "red",
          onClick: () => navigate("/admin/due_dates/expired"),
        }
      : userSettings.notifications.dueDateExpiringSoon && userSettings.notifications.dueDateExpiringSoon > 0
        ? {
            number: userSettings.notifications?.dueDateExpiringSoon,
            color: "yellow",
            onClick: () => navigate("/admin/due_dates/expiring_soon"),
          }
        : undefined
    : undefined;

  if (userSettings.permissions.dueDates.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/due_dates"
        text={i18n.t.dueDates.plural}
        to="/admin/due_dates"
        icon={SvgCalendar}
        onClick={closeSidebar}
        notification={dueDateNotification}
      />,
    );
  }

  if (userSettings.permissions.highwayPasses.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/highway_passes"
        text={i18n.t.highwayPasses.plural}
        to="/admin/highway_passes"
        icon={SvgPin}
        onClick={closeSidebar}
      />,
    );
  }

  if (userSettings.permissions.leasingCompanies.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/leasing_companies"
        text={i18n.t.leasingCompanies.plural}
        to="/admin/leasing_companies"
        icon={VpnKey}
        onClick={closeSidebar}
      />,
    );
  }
  if (userSettings.permissions.mileages.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/mileages"
        text={i18n.t.mileages.plural}
        to="/admin/mileages"
        icon={SvgPin}
        onClick={closeSidebar}
      />,
    );
  }
  if (userSettings.permissions.maintenances.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/maintenances"
        text={i18n.t.maintenance.plural}
        to="/admin/maintenances"
        icon={SvgBuild}
        onClick={closeSidebar}
      />,
    );
  }
  if (userSettings.permissions.suppliers.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/suppliers"
        text={i18n.t.suppliers.plural}
        to="/admin/suppliers"
        icon={SvgCarTag}
        onClick={closeSidebar}
      />,
    );
  }
  if (userSettings.permissions.claims.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/claims"
        text={i18n.t.claims.plural}
        to="/admin/claims"
        icon={SvgCarCrash}
        onClick={closeSidebar}
      />,
    );
  }
  if (userSettings.permissions.fines.read) {
    administrationCars.push(
      <Nav.Item
        key="/admin/fines"
        text={i18n.t.fines.plural}
        to="/admin/fines"
        icon={SvgWarning}
        onClick={closeSidebar}
      />,
    );
  }

  const carNotifications =
    taskNotification?.color || dueDateNotification?.color
      ? { color: taskNotification?.color ?? dueDateNotification?.color! }
      : undefined;

  return (
    <Nav.Item
      text={i18n.t.cars.plural}
      to={userSettings.permissions.cars.read ? "/admin/cars" : undefined}
      icon={SvgTrafficJam}
      onClick={closeSidebar}
      groupId="menu_cars"
      notification={carNotifications}
    >
      {administrationCars.length ? <Nav.List>{administrationCars}</Nav.List> : undefined}
    </Nav.Item>
  );
};

export default CarsMenu;
